import * as THREE from 'three';
import { GLTFLoader } from 'gltf-loader';
import { MeshoptDecoder } from 'meshopt-decoder';
import { WiggleBonesManager } from "./managers/wiggleBonesManager.js";
import { RenderingUtils } from '../../../utils/renderingUtils.js';
import AnimationManager from './managers/animationManager.js';
import BlendshapeManager from './managers/blendshapeManager.js';
import { RiggedManager } from './managers/riggedManager.js';

export class Model {
    /// CONSTRUCTEUR ET INITIALISATION
    constructor(scene, environment, materialManager, config, startAnimationName, enableWiggle , settings) {
       //console.log('🏗️ Model constructor appelé pour:', config?.modelName || 'Unknown');
        
        // Nouveau format unifié uniquement
        this.config = config;

        // Vérification de sécurité
        if (!this.config || !this.config.modelUrl) {
            console.error('Model constructor: config ou modelUrl manquant', this.config);
            return;
        }

        this.modelUrl = this.config.modelUrl;
        this.animationConfig = this.config.animation || null;
        this.startAnimationName = this.animationConfig?.animationName || this.config.startAnimationName || startAnimationName;
        this.enableWiggle = enableWiggle;

        this.settings = settings || {}; // Paramètres additionnels pour la rotation X
        this.scene = scene;
        this.environment = environment;
        this.materialManager = materialManager;
        this.model = null;
        this.animationManager = null;

        // Callback pour quand le modèle est chargé
        this.onModelLoaded = null;

    // Wiggle bones manager
    this.wiggleBonesManager = null;

    // Blendshape manager
    this.blendshapeManager = null;

    // Rigged manager pour l'animation de bones via MediaPipe
    this.riggedManager = null;

        // Système de sous-objets (maintenant le seul système de tracking)
        this.subObjects = this.config?.subObjects || [];
        this.subObjectInstances = []; // Références aux objets Three.js trouvés

        // Système d'initialisation basé sur MediaPipe (activé seulement si on a des sous-objets avec tracking)
        this.isInitialized = false;
        this.waitingForInitialCoords = this.subObjects.some(sub => sub.tracking) ? true : false;


        // Caches pour l'interpolation

        this.tempVector3 = new THREE.Vector3();
        this.tempQuaternion = new THREE.Quaternion();


        // Objets temporaires pour les calculs de matrice
        this.tempMatrix4 = new THREE.Matrix4();


        // Préparer le matériau cutout de segmentation si disponible
        this.cutoutSegmentationMat = this.materialManager?.createCutoutSegmentationMaterial ? this.materialManager.createCutoutSegmentationMaterial() : null;

        if (this.modelUrl) {
            this.loadModel(this.modelUrl);
        }
    }

    /// CHARGEMENT DU MODELE GLTF
    async loadModel(url) {
       //console.log('📥 LoadModel appelé pour:', this.config?.modelName, 'URL:', url);
        
        const loader = new GLTFLoader();
        
        // Configuration du décodeur Meshopt pour les fichiers compressés
        loader.setMeshoptDecoder(MeshoptDecoder);

        try {
            const gltf = await loader.loadAsync(url);
            this.model = gltf.scene;
            this.scene.add(this.model);            // Configuration avec le système unifié
            this.model.name = this.config?.modelName || "gltfScene";
            this.model.visible = false; // Toujours invisible par défaut
            
            // Désactiver le frustum culling pour tout le modèle
            this.disableFrustumCulling(this.model);

            this.environment?.updateEnvironmentMap();// Cache des animations
          
            // SI LE MODEL A DES ANIMATIONS
            if (gltf.animations?.length > 0) {
              //  console.log('[Model] Animations trouvées dans le GLTF:', gltf.animations.map(a => a.name));
                this.model.animations = gltf.animations;
                
                // Passer la configuration d'animations depuis les settings
                const animationsConfig = this.config?.animations || null;
                const modelAnimationName = this.config?.animationName || null;
                this.animationManager = new AnimationManager(this.model, animationsConfig, modelAnimationName);
           
            } else {
              //  console.log('[Model] Pas d\'animations dans le GLTF.');
            }
            //SI LE MODEL EST EN OVERLAY 
            const isOverlay = this.config?.options?.overlay === true;
            this.applyOverlay(isOverlay);

          //  SI LE MODEL A DES WIGGLE BONES
            if (this.enableWiggle && this.config.wiggleBones && this.config.wiggleBones.length > 0) {
                const wiggleConfig = {
                    wiggleBones: this.config.wiggleBones
                };
                this.wiggleBonesManager = new WiggleBonesManager(this.model, wiggleConfig);
            }

            // Initialiser le BlendshapeManager
            this.blendshapeManager = new BlendshapeManager(this.model, {
                interpolationSpeed: 0.5, // Plus rapide (0.1 par défaut)
                threshold: 0.001,
                ...this.config?.blendshapes
            });

      
            
            // Initialiser le RiggedManager si riggedConfig est présent et enabled
            if (this.config?.riggedConfig?.enabled) {
      
                
                try {
                    this.riggedManager = new RiggedManager(
                        this.model, 
                        this.config.boneMapping || {},  // boneMapping optionnel maintenant
                        this.config.riggedConfig || {}
                    );

                    // Créer l'UI si guiManager est disponible (sera passé via callback)
                    this.riggedManagerReadyForUI = true;
                   
                } catch (error) {
                    console.error('🦴 ❌ ERREUR lors de la création du RiggedManager:', error);
                }
            } 
          
            // Initialiser les sous-objets
            this.initializeSubObjects();

            // Appeler le callback si défini (pour les remplacements de matériaux)
            if (this.onModelLoaded && typeof this.onModelLoaded === 'function') {
                this.onModelLoaded(this.model);
            }

        } catch (error) {
            console.error('Error loading model:', error);
        }
    }

    /// INITIALISATION DES SOUS-OBJETS
    initializeSubObjects() {
        if (!this.model || !this.subObjects.length) return;

        this.subObjectInstances = [];

        this.subObjects.forEach((subObjectConfig) => {
            // Si objectName est vide ou inexistant, utiliser directement la racine
            if (!subObjectConfig.objectName || subObjectConfig.objectName.trim() === '') {
               //console.log(`✅ Utilisation de la racine du modèle (objectName vide)`);
                // Désactiver le frustum culling pour la racine
                this.disableFrustumCulling(this.model);
                this.subObjectInstances.push({
                    object: this.model,
                    config: subObjectConfig,
                    tempVector3: new THREE.Vector3(),
                    tempQuaternion: new THREE.Quaternion(),
                    tempMatrix4: new THREE.Matrix4()
                });
            } else {
                // Si objectName est présent, essayer de le trouver
                const foundObject = this.findObjectByName(this.model, subObjectConfig.objectName);
                if (foundObject) {
                   // console.log(`✅ Sous-objet trouvé: ${subObjectConfig.objectName}`);
                    // Désactiver le frustum culling pour ce sous-objet
                    this.disableFrustumCulling(foundObject);
                    this.subObjectInstances.push({
                        object: foundObject,
                        config: subObjectConfig,
                        tempVector3: new THREE.Vector3(),
                        tempQuaternion: new THREE.Quaternion(),
                        tempMatrix4: new THREE.Matrix4()
                    });
                } else {
                    // console.log(`⚠️ Sous-objet non trouvé: ${subObjectConfig.objectName}`);
                    // Ne rien ajouter à subObjectInstances dans ce cas
                }
            }
        });
    }

    /// RECHERCHE D'UN OBJET PAR NOM DANS LA HIERARCHIE
    findObjectByName(parent, name) {
        if (parent.name === name) {
            return parent;
        }

        for (let child of parent.children) {
          
            const found = this.findObjectByName(child, name);
            if (found) {
                return found;
            }
        }

        return null;
    }

    /// DESACTIVER LE FRUSTUM CULLING POUR UN OBJET ET SES ENFANTS
    disableFrustumCulling(object) {
        if (!object) return;

        // Parcourir récursivement tous les objets
        object.traverse((child) => {
            // Désactiver le frustum culling pour tous les mesh
            if (child.isMesh) {
                child.frustumCulled = false;
               //console.log(`🚫 Frustum culling désactivé pour: ${child.name || 'mesh sans nom'}`);
            }
            // Désactiver également pour les autres types d'objets si nécessaire
            if (child.isSkinnedMesh) {
                child.frustumCulled = false;
               //console.log(`🚫 Frustum culling désactivé pour SkinnedMesh: ${child.name || 'skinned mesh sans nom'}`);
            }
        });
    }

    /// INITIALISATION DEPUIS MEDIAPIPE
    initializeFromMediaPipe(coordsResults) {
        if (!this.model || !this.subObjectInstances.length) return;

        // Initialiser chaque sous-objet
        this.subObjectInstances.forEach((subObjectInstance) => {
            const { object, config } = subObjectInstance;
            const tracking = config.tracking;

            if (!tracking) return;

            // Position initiale
            if (tracking.positionReference) {
                const initialPosition = this.getNestedProperty(coordsResults, tracking.positionReference);
                if (initialPosition) {
                    
                    object.position.set(initialPosition.x, initialPosition.y, initialPosition.z);
                }
            }

            // Rotation initiale
            if (tracking.rotationReference) {
                const initialRotation = this.getNestedProperty(coordsResults, tracking.rotationReference);
                if (initialRotation) {
                    const tempQuaternion = new THREE.Quaternion();
                    tempQuaternion.copy(initialRotation);
                    object.quaternion.copy(tempQuaternion);
                }
            }

            // Échelle initiale
            if (tracking.scaleReference) {
                const initialScale = this.getNestedProperty(coordsResults, tracking.scaleReference);
               // console.log(tracking.scaleReference, initialScale)
                if (initialScale !== undefined && initialScale !== null) {
                    const modelScale = 1;
                    object.scale.set(
                        initialScale / modelScale,
                        initialScale / modelScale,
                        initialScale / modelScale
                    );
                }
            }

            // Matrice faciale pour l'initialisation
            if (this.config.modelType === "face") {
                const matrix = this.getNestedProperty(coordsResults, "face.facialTransformationMatrix");
                if (matrix) {
                    RenderingUtils.applyMatrix(object, matrix, {
                        scale: this.getNestedProperty(coordsResults, tracking.scaleReference),
                        useMatrixPosition: tracking.positionReference ? false : true,
                        useMatrixRotation: tracking.rotationReference ? false : true
                    });
                   // console.log(`🎭 Matrice faciale initiale appliquée sur ${config.objectName}`);
                }
            }
        });
    }

    /// MISE A JOUR AVEC LE TRACKING
    updateWithTracking(coordsResults, deltaTime) {
        if (!this.model) {
            this.update(deltaTime);
            return;
        }

        // Initialisation si nécessaire
        if (this.waitingForInitialCoords && !this.isInitialized) {
            this.initializeFromMediaPipe(coordsResults);
            this.isInitialized = true;
        }

        // Gestion de la visibilité selon le type de modèle
        if (this.config.modelType === "face") {
            if (this.getNestedProperty(coordsResults, 'face.faceVisible')) {
                this.modelAppear();
            } else {
                this.modelDisappear();
            }
        }

        if (this.config.modelType === "pose") {
            if (this.getNestedProperty(coordsResults, 'pose.poseVisible')) {
                this.modelAppear();
            } else {
                this.modelDisappear();
            }
            
        }

        // Mise à jour des blendshapes si disponibles
        this.updateBlendshapes(coordsResults);

        // Mise à jour du rigged manager si disponible
        if (this.riggedManager) {
            this.riggedManager.update(coordsResults);
        }

        // Mise à jour de la visibilité des sous-objets basée sur les données de visibilité
        this.updateSubObjectsVisibility(coordsResults);

        // Mise à jour des sous-objets (seul système de tracking maintenant)
        this.updateSubObjects(coordsResults, deltaTime);

        // Mise à jour standard (animations, wiggle bones)
        this.update(deltaTime);
    }

    /// MISE À JOUR DE LA VISIBILITÉ DES SOUS-OBJETS
    updateSubObjectsVisibility(coordsResults) {
        if (!this.subObjectInstances.length) return;

        // Seuil de visibilité (configurable)
        const visibilityThreshold = this.config?.visibilityThreshold || 0.8;

        this.subObjectInstances.forEach((subObjectInstance) => {
            const { object, config } = subObjectInstance;
            const tracking = config.tracking;

            // Vérifier si le sous-objet a une référence de visibilité
            if (tracking.visibilityReference) {
                const visibilityValue = this.getNestedProperty(coordsResults, tracking.visibilityReference);
                
                if (visibilityValue !== null && visibilityValue !== undefined) {
                    // Appliquer le seuil de visibilité
                    const shouldBeVisible = visibilityValue >= visibilityThreshold;
                    
                    // Mettre à jour la visibilité de l'objet
                    if (object.visible !== shouldBeVisible) {
                        object.visible = shouldBeVisible;
                        
                        // Log optionnel pour debug
                        if (this.config?.debugVisibility) {
                          //  console.log(`👁️ ${config.objectName}: visibility=${visibilityValue.toFixed(3)}, visible=${shouldBeVisible} (threshold=${visibilityThreshold})`);
                        }
                    }
                }
            }
        });
    }

    /// MISE A JOUR DES SOUS-OBJETS
    updateSubObjects(coordsResults, deltaTime) {
        if (!this.subObjectInstances.length) return;

        this.subObjectInstances.forEach((subObjectInstance) => {
            const { object, config, tempVector3, tempQuaternion, tempMatrix4 } = subObjectInstance;
            const tracking = config.tracking;
            const lerp = config.lerp || { position: 0.7, rotation: 0.3, scale: 0.3 };

            // Récupérer les données de tracking pour ce sous-objet
            const position = tracking.positionReference ?
                this.getNestedProperty(coordsResults, tracking.positionReference) : null;
            const rotation = tracking.rotationReference ?
                this.getNestedProperty(coordsResults, tracking.rotationReference) : null;
            const scale = tracking.scaleReference ?
                this.getNestedProperty(coordsResults, tracking.scaleReference) : null;

            // Vérifier si useMatrix est explicitement défini à false (cela affecte seulement la rotation)
            const useMatrixRotation = tracking.useMatrix !== false; // Par défaut true, sauf si explicitement false

            // Récupérer la matrice faciale si le modèle est de type face (toujours récupérée)
            const matrix = this.config.modelType === "face" ?
                this.getNestedProperty(coordsResults, "face.facialTransformationMatrix") : null;

            // Appliquer les transformations
            if (position || matrix) {
                if (position) {
                    tempVector3.set(position.x, position.y, position.z);
                }

                if (matrix) {
                    // Utiliser la matrice avec lerp pour un mouvement plus fluide
                    tempMatrix4.extractRotation(matrix instanceof THREE.Matrix4 ? matrix : new THREE.Matrix4().fromArray(matrix.data));
                    
                    // Appliquer la matrice avec lerp
                    const matrixOptions = {
                        scale: scale / this.config.modelScale,
                        position: tempVector3,
                        rotation: rotation,
                        useMatrixPosition: tracking.positionReference ? false : true,
                        useMatrixRotation: useMatrixRotation && (tracking.rotationReference ? false : true), // useMatrix:false désactive seulement la rotation
                        lerp: lerp // Ajouter le lerp pour les transformations basées sur la matrice
                    };
                    
                    RenderingUtils.applyMatrix(object, matrix, matrixOptions);
                } else {
                    // Mode sans matrice du tout : mise à jour manuelle avec lerp
                    // Mise à jour de la position
                    if (position && !object.position.equals(tempVector3)) {
                        object.position.lerp(tempVector3, lerp.position);
                    }
                    // console.log(object.name + " position.z ->"+ object.position.z)

                    // Mise à jour de la rotation
                    if (rotation) {
                        tempQuaternion.copy(rotation);
                        object.quaternion.slerp(tempQuaternion, lerp.rotation);
                        if (config.constraints && config.constraints.rotationXMin !== undefined && config.constraints.rotationXMax !== undefined) {
                            const constrainedQuaternion = this.applyRotationXConstraints(object.quaternion, config.constraints.rotationXMin, config.constraints.rotationXMax);

                            object.quaternion.copy(constrainedQuaternion);
                        }
                    }

                    // Mise à jour de l'échelle
                    if (scale !== undefined && scale !== null) {
                        const modelScale = 1;
                       
                        tempVector3.set(
                            scale / modelScale,
                            scale / modelScale,
                            scale / modelScale
                        );
                
                        object.scale.lerp(tempVector3, lerp.scale);
                    }
                }
            }
        });
    }

    /// MISE À JOUR DES BLENDSHAPES
    updateBlendshapes(coordsResults) {
        if (!this.blendshapeManager) return;

        // Récupérer les blendshapes depuis les résultats MediaPipe
        const faceResults = this.getNestedProperty(coordsResults, 'face');
        if (!faceResults || !faceResults.hasBlendshapes || !faceResults.hasBlendshapes()) {
            return;
        }

        const blendshapes = faceResults.getBlendshapes();
        if (!blendshapes) return;

        // Appliquer les blendshapes au modèle
        // Les blendshapes MediaPipe sont déjà normalisés (0-1)
        // immediate=true pour un rendu instantané, false pour interpolation douce
        this.blendshapeManager.setBlendshapes(blendshapes, true); // true = immédiat
    }

    applyRotationXConstraints(quaternion, minXDegrees, maxXDegrees) {
        // Convertir en radians
        const minXRad = minXDegrees * Math.PI / 180;
        const maxXRad = maxXDegrees * Math.PI / 180;

        // Extraire les angles d'Euler en préservant l'ordre YXZ
        const euler = new THREE.Euler().setFromQuaternion(quaternion, 'YXZ');

        // Sauvegarder les valeurs Y et Z originales
        const originalY = euler.y;
        const originalZ = euler.z;

        // Contraindre seulement l'axe X
        euler.x = Math.max(minXRad, Math.min(maxXRad, euler.x));

        // Réassigner les valeurs Y et Z pour s'assurer qu'elles ne changent pas
        euler.y = originalY;
        euler.z = originalZ;

        // Reconvertir en quaternion avec le même ordre
        const constrainedQuaternion = new THREE.Quaternion().setFromEuler(euler);

        return constrainedQuaternion;
    }



    /// UPDATE STANDARD (ANIMATIONS, WIGGLE BONES)
    update(deltaTime) {
         // Update animation via AnimationManager
        if (this.animationManager) {
            this.animationManager.update(deltaTime);
        }
        // Update wiggle bones via manager
        if (this.enableWiggle && this.wiggleBonesManager) {
            this.wiggleBonesManager.update();
        }
        // Update blendshapes via manager
        if (this.blendshapeManager) {
            this.blendshapeManager.update();
        }
       
    }

 

    /// APPARITION DU VISAGE
    modelAppear() {
        if (!this.model) return;
        if (this.wasVisible === true) return; // Ne rien faire si déjà visible
        this.wasVisible = true;
        this.model.visible = true;
        
        // Lancer l'animation avec l'index de clip 0 quand le modèle apparaît
        if (this.animationManager && this.animationManager.clipNames.length > 0) {
            this.animationManager.playClipByIndex(0);
            //console.log('🎬 Animation lancée au clip index 0 lors de l\'apparition du modèle');
        }
        
        // Animations gérées par AnimationManager uniquement
    }

    /// DISPARITION DU VISAGE
    modelDisappear() {
        if (!this.model) return;
        if (this.wasVisible === false) return; // Ne rien faire si déjà invisible
        this.wasVisible = false;
        this.model.visible = false;
        this.isInitialized = false;
    }

  
    /// NETTOYAGE DES ANIMATIONS
    dispose() {
       //console.log(`🗑️ Dispose du modèle: ${this.config?.modelName}`);
        
        // Nettoyage désormais géré par AnimationManager
        if (this.animationManager) {
            this.animationManager.dispose();
            this.animationManager = null;
        }
        
        // Nettoyage du BlendshapeManager
        if (this.blendshapeManager) {
            this.blendshapeManager.dispose();
            this.blendshapeManager = null;
        }
        
        // Nettoyage du WiggleBonesManager
        if (this.wiggleBonesManager) {
           //console.log(`🧹 Nettoyage WiggleBonesManager pour: ${this.config?.modelName}`);
            this.wiggleBonesManager = null;
        }
        
        // Nettoyage du RiggedManager
        if (this.riggedManager) {
           //console.log(`🧹 Nettoyage RiggedManager pour: ${this.config?.modelName}`);
            this.riggedManager.dispose();
            this.riggedManager = null;
        }
        
        // Réinitialiser les références
        this.model = null;
        this.config = null;
    }    
 
    /// APPLIQUER LE MODE OVERLAY
    applyOverlay(isOverlay) {
        if (!this.model || !isOverlay) return;
       //console.log(`🌟 Application du mode overlay pour le modèle ${this.model.name}`);
        RenderingUtils.makeOverlayObject(this.model, {
            renderOrder: 1000, // Plus élevé que le plan de segmentation (500)
            depthTest: false,
            depthWrite: false,


        }
        );
    }

    /// ACCES RAPIDE AUX PROPRIETES NESTEES
    getNestedProperty(obj, path) {
        if (!obj || !path) return null;

        return path.split('.').reduce((current, key) => {
            return current && current[key] !== undefined ? current[key] : null;
        }, obj);
    }

    /// METHODES PRATIQUES POUR LES BLENDSHAPES
    /**
     * Définit un blendshape spécifique
     * @param {string} name - Nom du blendshape
     * @param {number} value - Valeur entre 0 et 1
     * @param {boolean} immediate - Si true, applique immédiatement
     */
    setBlendshape(name, value, immediate = false) {
        if (this.blendshapeManager) {
            this.blendshapeManager.setBlendshape(name, value, immediate);
        }
    }

    /**
     * Définit plusieurs blendshapes en une fois
     * @param {Object} values - Objet avec les noms et valeurs des blendshapes
     * @param {boolean} immediate - Si true, applique immédiatement
     */
    setBlendshapes(values, immediate = false) {
        if (this.blendshapeManager) {
            this.blendshapeManager.setBlendshapes(values, immediate);
        }
    }

    /**
     * Récupère la valeur actuelle d'un blendshape
     * @param {string} name - Nom du blendshape
     * @returns {number|null} Valeur actuelle ou null
     */
    getBlendshape(name) {
        return this.blendshapeManager ? this.blendshapeManager.getBlendshape(name) : null;
    }

    /**
     * Remet tous les blendshapes à zéro
     * @param {boolean} immediate - Si true, applique immédiatement
     */
    resetBlendshapes(immediate = false) {
        if (this.blendshapeManager) {
            this.blendshapeManager.resetAll(immediate);
        }
    }

    /**
     * Retourne la liste des blendshapes disponibles
     * @returns {Array<string>} Liste des noms de blendshapes
     */
    getAvailableBlendshapes() {
        return this.blendshapeManager ? this.blendshapeManager.getAvailableBlendshapes() : [];
    }

    /**
     * Initialise l'UI du RiggedManager si disponible
     * @param {Object} guiManager - Le gestionnaire d'interface
     */
    initRiggedManagerUI(guiManager) {
        if (this.riggedManager && this.riggedManagerReadyForUI && guiManager) {
            console.log('🎛️ Initialisation UI RiggedManager pour:', this.config.modelName);
            this.riggedManager.createUI(guiManager);
            this.riggedManagerReadyForUI = false; // Éviter la double initialisation
        }
    }

    

   


}